import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild, } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectItem } from 'primeng/api';
import { firstValueFrom, Subscription } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { v4 as uuidv4 } from 'uuid';

import { ActivityDefaultsComponent } from '../activity-defaults/activity-defaults.component'
import { ChemistryComponent } from '../chemistry/chemistry.component';
import { ExpensesComponent } from '../expenses/expenses.component';
import { GoalsComponent } from '../goals/goals.component';
import { HeaderComponent } from '../header/header.component';
import { PerformanceComponent } from '../performance/performance.component';
import { SequenceComponent } from '../sequence/sequence.component'
import { SiteDetailComponent } from '../site-detail/site-detail.component';
import { WellInformationComponent } from '../well-information/well-information.component';

import {
  Simulation,
  SimulationActivity,
  SimulationFluidSummary,
  SimulationGoals,
  SimulationHeader,
  SimulationSiteDetails,
} from '../../models/simulation';
import { SimulationCalculations } from '../functions/simulationCalculations';
import { SimulationCommon } from '../functions/simulationCommon';
import { SimulationExport } from '../functions/simulationExport';
import { SimulationGraphs } from '../functions/simulationGraphs';

import { IApplicationContextService, ISimulationService, UserAuthorizationService } from '../../services/services';
import { first } from 'rxjs/operators';
import { CompanyClient, CompanyNameViewModel, PerformanceSummary, WellInfo } from '../../services/nswag/nswagclient';
import { IUserManagerService } from '../../services/iUserManager.service';

@Component({
    selector: 'app-job-simulator',
    templateUrl: './job-simulator.component.html',
    styleUrls: ['./job-simulator.component.css'],
    standalone: false
})
export class JobSimulatorComponent implements AfterViewInit, OnDestroy, OnInit {
  private _simulation: Simulation = new Simulation();
  private _companyContextSubscription?: Subscription;
  activeTab = 0;
  avData: [number, number][] = [];
  avOptions: any;
  company: CompanyNameViewModel | null = null;
  consumerCompanies: SelectItem[] = [];
  depthVsReData: [number, number, number][] = [];
  depthVsReOptions: any;
  flowbackBblsData: [number, number][] = [];
  flowbackBblsOptions: any;
  pumpedBblsData: [number, number][] = [];
  pumpedBblsOptions: any;
  simulationIdParam = '';
  totalChemicalCost: number = 0;
  totalServiceCost: number = 0;

  @Input()
  set simulation(sim: Simulation){
    this._simulation = sim;
    const defaults = sim.defaults ?? SimulationCommon.getFactorySimulationDefaults();
    const activities = sim.activities ?? [];
    const chemicals = sim.chemicals ?? [];
    const fluidSummary = sim.fluidSummary ?? {};
    const expenses = sim.expenses ?? [];
    const goals = sim.goals ?? {};
    const header = sim.header ?? { simulationId: sim.id, name: 'New Simulation', majorVersion: 1, minorVersion: 0, revisionVersion: 0 };
    const performance = sim.performance ?? new PerformanceSummary();
    const siteDetails = sim.siteDetails ?? {};
    const wellInfo = sim.wellInfo ?? this.createDefaultWellInfo();

    sim.defaults = defaults;
    sim.activities = activities;
    sim.chemicals = chemicals;
    sim.fluidSummary = fluidSummary;
    sim.expenses = expenses;
    sim.goals = goals;
    sim.header = header;
    sim.performance = performance;
    sim.siteDetails = siteDetails;
    sim.wellInfo = wellInfo;

    //Components
    this.actDfltCom.defaults = defaults;
    this.seqCom.activities = activities;
    this.seqCom.defaults = defaults;
    this.chemistryCom.chemicals = chemicals;
    this.chemistryCom.fluidSummary = fluidSummary;
    this.expensesCom.expenses = expenses;
    this.goalCom.goals = goals;
    this.headerCom.header = header;
    this.perfCom.performanceSummary = performance;
    this.siteCom.siteDetails = siteDetails;
    this.wellCom.wellInfo = wellInfo;
    this.wellCom.setSelectedTubingODs();
  }
  get simulation(): Simulation{
    return this._simulation;
  }

  @ViewChild(ActivityDefaultsComponent, { static: true })
  private actDfltCom!: ActivityDefaultsComponent;
  @ViewChild(ChemistryComponent, { static: true })
  private chemistryCom!: ChemistryComponent;
  @ViewChild(ExpensesComponent, { static: true })
  private expensesCom!: ExpensesComponent;
  @ViewChild(GoalsComponent, { static: true })
  private goalCom!: GoalsComponent;
  @ViewChild(HeaderComponent, { static: true })
  private headerCom!: HeaderComponent;
  @ViewChild(PerformanceComponent, { static: true })
  private perfCom!: PerformanceComponent;
  @ViewChild(SequenceComponent, { static: true })
  private seqCom!: SequenceComponent;
  @ViewChild(SiteDetailComponent, { static: true })
  private siteCom!: SiteDetailComponent;
  @ViewChild(WellInformationComponent, { static: true })
  public wellCom!: WellInformationComponent;

  constructor(
    private _applicationContextService: IApplicationContextService,
    private _companyService: CompanyClient,
    private _route: ActivatedRoute,
    private _router: Router,
    private _simulationService: ISimulationService,
    private _titleService: Title,
    private _userAuthorizationService: UserAuthorizationService,
    private _userManagerService: IUserManagerService
  ) { }

  ngOnInit() {
    this.avOptions = SimulationGraphs.buildBasicOptions('Time', 'AV', 'Relative Time', 'Velocity', 'AV vs Time');
    this.flowbackBblsOptions = SimulationGraphs.buildBasicOptions('Time', 'Flowback', 'Relative Time', 'BBLS', 'Flowback (BBLs) vs Time');
    this.pumpedBblsOptions = SimulationGraphs.buildBasicOptions('Time', 'Pumped', 'Relative Time', 'BBLS', 'Pumped (BBLs) vs Time');

    this.depthVsReOptions = {
      width: 'auto',
      labels: ['Time','Depth','RE'],
      xlabel: 'Relative Time',
      ylabel: 'Feet',
      series: {'RE': {axis: 'y2'}},
      title: 'Depth & RE vs Time',
      animatedZooms: true,
      pointSize: 4
    };
    this.listenToCompanyContext();
  }

  ngOnDestroy() {
    this._companyContextSubscription?.unsubscribe();
  }

  async ngAfterViewInit() : Promise<void> {
    const params = await firstValueFrom(this._route.params.pipe(first()));
    this.simulationIdParam = typeof params['id'] === 'string' ? params['id'] : '';
    const sim = await this.getSimulationFromRoute(this.simulationIdParam);
    if(!sim) {
      this.navigateToSimulationList();
      return;
    }
    this.simulation = sim;
    this.consumerCompanies = await this.getConsumerCompanies();
    this.headerCom.consumerCompanies = this.consumerCompanies;
    this.setPageTitle(this.simulation.header?.name ?? '');
    await this.updateCompany();
  }

  async cloneSimulation() {
    this.simulation = SimulationCommon.cloneSimulation(this.simulation);
    await this.saveSimulation(false);
  }

  createEmptySimulation() : Simulation {
    const simId = uuidv4();
    const def = SimulationCommon.getFactorySimulationDefaults();
    const well = this.createDefaultWellInfo();
    const acts = SimulationCommon.getInitialActivities(def, well);
    const fld: SimulationFluidSummary = { fluidTypeId: 'SLW', loopTypeId: 'recirculate' };
    const goal: SimulationGoals = {};
    const hdr: SimulationHeader = { simulationId: simId, name: 'New Simulation', majorVersion: 1, minorVersion: 0, revisionVersion: 0 };
    const perf = new PerformanceSummary();
    const site: SimulationSiteDetails = {};
    if(!hdr.customer && this.consumerCompanies && this.consumerCompanies.length > 0) {
      hdr.customer = this.consumerCompanies[0].value as CompanyNameViewModel;
    }
    const sim = new Simulation();
    sim.id = simId;
    sim.activities = acts;
    sim.chemicals = [];
    sim.defaults = def;
    sim.expenses = [];
    sim.fluidSummary = fld;
    sim.goals = goal;
    sim.header = hdr;
    sim.performance = perf;
    sim.siteDetails = site;
    sim.wellInfo = well;
    return sim;
  }

  async generateActivitiesFromPlugs() {
    const defaults = this.simulation.defaults ?? SimulationCommon.getFactorySimulationDefaults();
    const plugSets = this.wellCom.wellInfo.plugSets ?? [];
    const initAct: SimulationActivity[] = SimulationCommon.getInitialActivities(defaults, this.wellCom.wellInfo);
    const plugActivities: SimulationActivity[] = SimulationCommon.getActivitiesFromPlugs(plugSets, defaults);
    if(initAct.length === 6) {
      this.seqCom.activities = [initAct[0], initAct[1], initAct[2], initAct[3], ...plugActivities, initAct[4], initAct[5]];
    }
    else {
      this.seqCom.activities = [...initAct, ...plugActivities];
    }
    await this.runCalculatedFields();
  }

  async getConsumerCompanies() : Promise<SelectItem[]> {
    try{
      const cmpns = await firstValueFrom(this._companyService.getConsumerCompanyNames());
      return cmpns.map((r) => <SelectItem> { label: r.companyName ?? '', value: r });
    }
    catch(e) {
      console.error('Error encountered retrieving the Operator companies', [e]);
      return [];
    }
  }

  async getSimulation(simulationId: string) : Promise<Simulation> {
    return await firstValueFrom(this._simulationService.getSimulation(simulationId));
  }

  async getSimulationFromRoute(simulationId: string) : Promise<Simulation | null> {
    if (simulationId) {
      try {
        const resultSim = await this.getSimulation(simulationId);
        if(this.company && resultSim.header?.companyId !== this.company.companyId) {
          this.navigateToSimulationList();
          return null;
        }
        return resultSim;
      }
      catch(e) {
        console.error(`Could not retrieve simulation (id = ${simulationId})`, [e]);
        return null;
      }
    }
    return this.createEmptySimulation();
  }

  getTotalChemicalCost(): number {
    return this.chemistryCom.chemicals
      .reduce((acc, c) => {
        return acc + SimulationCalculations.calculateChemicalCost(
          c,
          this.seqCom.activities,
          this.chemistryCom.fluidSummary.loopTypeId === 'recirculate',
          this.chemistryCom.fluidSummary.barrelCount ?? 0,
          this.wellCom.wellInfo.plugSets ?? []);
      }, 0);
  }

  getTotalServiceCost() : number {
    const act = this.seqCom.activities[this.seqCom.activities.length - 1] ?? {};
    const plugCount = SimulationCommon.getTotalPlugs(this.wellCom.wellInfo.plugSets ?? []);
    return this.expensesCom.expenses.reduce((acc, e) => acc + SimulationCalculations.calculateServiceCost(e, act, plugCount), 0);
  }

  async handleTabChange(value: string | number | undefined) {
    const index: number = typeof value === 'number' ? value : Number.parseInt(value ?? '0', 10);
    if(index > 0) {
      await this.runCalculatedFields();
      if(index > 1) {
        this.updateOutput();
      }
    }
  }

  async isEditorOrAdmin(companyId: string) : Promise<boolean> {
    try {
      const user = await firstValueFrom(this._userManagerService.getCurrentUser());
      return this._userAuthorizationService.isUserEditorOrAdmin(user, companyId, false);
    }
    catch(e) {
      console.error('Could not get user', [e]);
      this.navigateToSimulationList();
      return false;
    }
  }

  listenToCompanyContext() {
    this._companyContextSubscription = this._applicationContextService.companyContextObserver.subscribe({
      next: async c => await this.updateCompany(c),
    });
  }

  navigateToSimulationList() {
    this._router.navigate(['/simulator']);
  }

  async runCalculatedFields() {
    this.seqCom.activities = SimulationCommon.updateInitialActivityDepths(this.seqCom.activities, this.wellCom.wellInfo);
    this.seqCom.activities = await SimulationCalculations.calculateFields(this.chemistryCom.fluidSummary, this.wellCom.wellInfo, this.seqCom.activities);
    this.avData = await SimulationGraphs.buildGraphData(this.seqCom.activities, SimulationGraphs.avDataMapping);
    this.depthVsReData = await SimulationGraphs.buildDepthGraphData(this.seqCom.activities);
    this.flowbackBblsData = await SimulationGraphs.buildGraphData(this.seqCom.activities, SimulationGraphs.flowbackBblsDataMapping);
    this.pumpedBblsData = await SimulationGraphs.buildGraphData(this.seqCom.activities, SimulationGraphs.pumpedBblsDataMapping);
  }

  setPageTitle(name: string) {
    if (!name || name === '') {
      this._titleService.setTitle('New Simulation');
    } else { this._titleService.setTitle(name); }
  }

  async updateCompany(cmpny: CompanyNameViewModel | null = null)  {
    if(!cmpny) {
      cmpny = this._applicationContextService.getCompanyContext();
    }
    if (cmpny) {
      if (!this.company || cmpny.companyId === this.company.companyId) {
        this.company = cmpny;
        if(this.simulationIdParam && this.simulation.header?.companyId !== this.company.companyId) {
          this.navigateToSimulationList();
        }
        if (!this.company.companyId) {
          this.navigateToSimulationList();
          return;
        }
        const userCheck = await this.isEditorOrAdmin(this.company.companyId);
        if(!userCheck) {
          this._router.navigate(['/']);
        }
      }
      else {
        this.navigateToSimulationList();
      }
    }
  }

  updateOutput() {
    this.totalChemicalCost = this.getTotalChemicalCost();
    this.totalServiceCost = this.getTotalServiceCost();
    const plugCount = SimulationCommon.getTotalPlugs(this.wellCom.wellInfo.plugSets ?? []);
    const md = this.wellCom.wellInfo.md ?? 0;
    this.perfCom.performanceSummary = SimulationCommon.getPerformanceMetrics(this.seqCom.activities, plugCount, md);
  }

  async saveSimulation(navigateToList: boolean = true) {
    this.simulation.activities = this.seqCom.activities = await SimulationCalculations.calculateFields(this.chemistryCom.fluidSummary, this.wellCom.wellInfo, this.seqCom.activities);
    this.simulation.chemicals = this.chemistryCom.chemicals;
    this.simulation.defaults = this.actDfltCom.defaults;
    this.simulation.fluidSummary = this.chemistryCom.fluidSummary;
    this.simulation.expenses = this.expensesCom.expenses;
    this.simulation.goals = this.goalCom.goals;
    this.simulation.header = this.headerCom.header;
    const plugCount = SimulationCommon.getTotalPlugs(this.wellCom.wellInfo.plugSets ?? []);
    const md = this.wellCom.wellInfo.md ?? 0;
    this.simulation.performance = this.perfCom.performanceSummary = SimulationCommon.getPerformanceMetrics(this.seqCom.activities, plugCount, md); //Can be recalculated on load.
    this.simulation.siteDetails = this.siteCom.siteDetails;
    this.simulation.wellInfo = this.wellCom.wellInfo;

    if (!this.simulation.header) {
      this.simulation.header = {};
    }

    if(this.company?.companyId) {
      this.simulation.header.companyId = this.company.companyId;
    }
    this.simulation.header.simulationId = this.simulation.id;
    try{
      const saved = await firstValueFrom(this._simulationService.saveSimulation(this.simulation));
      const savedSimulationId = saved.header?.simulationId ?? '(unknown)';
      console.log(`Saved Simulation ${savedSimulationId}`);
      if(navigateToList) {
        this.navigateToSimulationList();
      }
    }
    catch(error) {
      console.error('An error occurred: ', [error]);
    }
  }

  async exportSimulation() {
    this.seqCom.activities = await SimulationCalculations.calculateFields(this.chemistryCom.fluidSummary, this.wellCom.wellInfo, this.seqCom.activities);
    this.updateOutput();
    const lastActivity: SimulationActivity = this.seqCom.activities[this.seqCom.activities.length - 1] ?? {};
    const stats = this.perfCom.performanceSummary ?? new PerformanceSummary();
    const sections: SelectItem[] = [
      { label: this.headerCom.header.name ?? 'Simulation', value: SimulationExport.headerCsvBlock(this.headerCom.header) },
      { label: 'Site Details', value: SimulationExport.siteDetailsCsvBlock(this.siteCom.siteDetails) },
      { label: 'Expenses', value: SimulationExport.expenseHeader + this.expensesCom.expenses.map(SimulationExport.expenseToCSVRow).join('\r\n') },
      { label: 'Chemicals', value: SimulationExport.chemicalHeader + this.chemistryCom.chemicals.map(SimulationExport.chemicalToCSVRow).join('\r\n') },
      { label: 'Activities', value: SimulationExport.activityHeader + this.seqCom.activities.map(SimulationExport.activityToCSVRow).join('\r\n') },
      { label: 'Metrics', value: SimulationExport.wellInfoToCsvBlock(this.wellCom.wellInfo) },
      { label: 'Job Totals', value: SimulationExport.jobTotalsCsvBlock(lastActivity) },
      { label: 'Statistics', value: SimulationExport.statisticsCsvBlock(stats) },
      { label: 'Cost Estimates', value: SimulationExport.costEstimateCsvBlock(this.totalChemicalCost, this.totalServiceCost) },
    ];
    await SimulationExport.exportToCSV(sections);
  }

  private createDefaultWellInfo(): WellInfo {
    return new WellInfo({ xoEot: 0, casings: [], plugs: [], plugSets: [] });
  }
}
